import React from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  Grid, 
  Chip, 
  Tabs, 
  Tab,
  LinearProgress 
} from '@material-ui/core';
import { 
  PolicyOutlined, 
  CheckCircle, 
  ErrorOutline, 
  Warning 
} from '@material-ui/icons';
import { 
  ProjectBranchPolicyReport, 
  BranchPolicyCompliance 
} from '../types';
import { 
  getComplianceIcon, 
  getComplianceChip, 
  getComplianceColor,
  formatDate, 
  formatDateShort 
} from '../utils';
import { TabPanel } from '../shared';

// Policy overview cards component
interface PolicyOverviewCardsProps {
  policyReport: ProjectBranchPolicyReport;
}

export const PolicyOverviewCards: React.FC<PolicyOverviewCardsProps> = ({ policyReport }) => (
  <Grid container spacing={3} style={{ marginBottom: '24px' }}>
    <Grid item xs={12} sm={6} md={3}>
      <Card elevation={2}>
        <CardContent style={{ textAlign: 'center', padding: '20px' }}>
          <PolicyOutlined style={{ fontSize: 40, color: '#2196f3', marginBottom: '8px' }} />
          <Typography variant="h4" style={{ fontWeight: 'bold', color: '#2196f3' }}>
            {policyReport.totalRepositories}
          </Typography>
          <Typography variant="body2" color="textSecondary">Total Repositories</Typography>
          <Typography variant="caption" style={{ color: '#4caf50' }}>
            {policyReport.compliantRepositories} compliant
          </Typography>
        </CardContent>
      </Card>
    </Grid>
    <Grid item xs={12} sm={6} md={3}>
      <Card elevation={2}>
        <CardContent style={{ textAlign: 'center', padding: '20px' }}>
          <CheckCircle style={{ fontSize: 40, color: '#4caf50', marginBottom: '8px' }} />
          <Typography variant="h4" style={{ fontWeight: 'bold', color: '#4caf50' }}>
            {policyReport.overallComplianceRate.toFixed(1)}%
          </Typography>
          <Typography variant="body2" color="textSecondary">Compliance Rate</Typography>
          <Typography variant="caption" color="textSecondary">
            Overall project rate
          </Typography>
        </CardContent>
      </Card>
    </Grid>
    <Grid item xs={12} sm={6} md={3}>
      <Card elevation={2}>
        <CardContent style={{ textAlign: 'center', padding: '20px' }}>
          <ErrorOutline style={{ fontSize: 40, color: '#f44336', marginBottom: '8px' }} />
          <Typography variant="h4" style={{ fontWeight: 'bold', color: '#f44336' }}>
            {policyReport.nonCompliantRepositories}
          </Typography>
          <Typography variant="body2" color="textSecondary">Non-Compliant</Typography>
          <Typography variant="caption" color="textSecondary">
            Need attention
          </Typography>
        </CardContent>
      </Card>
    </Grid>
    <Grid item xs={12} sm={6} md={3}>
      <Card elevation={2}>
        <CardContent style={{ textAlign: 'center', padding: '20px' }}>
          <Warning style={{ fontSize: 40, color: '#ff9800', marginBottom: '8px' }} />
          <Typography variant="h4" style={{ fontWeight: 'bold', color: '#ff9800' }}>
            {policyReport.summary.commonViolations.length}
          </Typography>
          <Typography variant="body2" color="textSecondary">Common Issues</Typography>
          <Typography variant="caption" color="textSecondary">
            Across repositories
          </Typography>
        </CardContent>
      </Card>
    </Grid>
  </Grid>
);

// Repository compliance list component
interface RepositoryComplianceListProps {
  repositoryCompliance: BranchPolicyCompliance[];
  onRepositorySelect: (repository: BranchPolicyCompliance) => void;
}

export const RepositoryComplianceList: React.FC<RepositoryComplianceListProps> = ({ 
  repositoryCompliance, 
  onRepositorySelect 
}) => (
  <Grid container spacing={3}>
    {repositoryCompliance.map((repo, index) => (
      <Grid item xs={12} md={6} lg={4} key={index}>
        <Card 
          elevation={1} 
          style={{ 
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onClick={() => onRepositorySelect(repo)}
        >
          <CardContent>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
              <div style={{ flex: 1 }}>
                <Typography variant="h6" style={{ marginBottom: '4px' }}>
                  {repo.repositoryName}
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: '8px' }}>
                  Last checked: {formatDateShort(repo.lastChecked)}
                </Typography>
              </div>
              {getComplianceIcon(repo.overallCompliance)}
            </div>
            
            <div style={{ marginBottom: '12px' }}>
              {getComplianceChip(repo.overallCompliance)}
            </div>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Master Branch</Typography>
                <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                  {getComplianceChip(repo.masterBranchCompliance.complianceStatus)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Release Branch</Typography>
                <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                  {getComplianceChip(repo.releaseBranchCompliance.complianceStatus)}
                </Typography>
              </Grid>
            </Grid>

            <div style={{ marginTop: '12px' }}>
              <Typography variant="caption" color="textSecondary">
                Issues: {repo.masterBranchCompliance.complianceStatus === 'non_compliant' ? 1 : 0} + {repo.releaseBranchCompliance.details.filter((d: string) => !d.includes('compliant')).length}
              </Typography>
            </div>
          </CardContent>
        </Card>
      </Grid>
    ))}
  </Grid>
);

// Common violations component
interface CommonViolationsProps {
  commonViolations: ProjectBranchPolicyReport['summary']['commonViolations'];
  totalRepositories: number;
}

export const CommonViolations: React.FC<CommonViolationsProps> = ({ commonViolations, totalRepositories }) => (
  <div>
    <Typography variant="h6" style={{ marginBottom: '16px' }}>
      Most Common Policy Violations
    </Typography>
    {commonViolations.map((violation: ProjectBranchPolicyReport['summary']['commonViolations'][number], index: number) => (
      <Card key={index} style={{ marginBottom: '12px' }} elevation={1}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                {violation.violation}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Chip 
                size="small" 
                label={`${violation.count} repositories`}
                style={{ backgroundColor: '#f44336', color: 'white' }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Typography variant="body2" color="textSecondary">
                {((violation.count / totalRepositories) * 100).toFixed(1)}% affected
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="textSecondary">
                Affected repositories: {violation.repositories.slice(0, 5).join(', ')}
                {violation.repositories.length > 5 && ` and ${violation.repositories.length - 5} more...`}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    ))}
    {commonViolations.length === 0 && (
      <Card elevation={1}>
        <CardContent style={{ textAlign: 'center', padding: '40px' }}>
          <CheckCircle style={{ fontSize: 60, color: '#4caf50', marginBottom: '16px' }} />
          <Typography variant="h6" style={{ marginBottom: '8px' }}>
            No Common Violations Found
          </Typography>
          <Typography variant="body2" color="textSecondary">
            All repositories are following the branch policy guidelines
          </Typography>
        </CardContent>
      </Card>
    )}
  </div>
);

// Summary report component
interface SummaryReportProps {
  policyReport: ProjectBranchPolicyReport;
}

export const SummaryReport: React.FC<SummaryReportProps> = ({ policyReport }) => (
  <Grid container spacing={3}>
    <Grid item xs={12} md={6}>
      <Card elevation={1}>
        <CardContent>
          <Typography variant="h6" style={{ marginBottom: '16px' }}>
            Compliance Summary
          </Typography>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Total Repositories:</Typography>
              <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                {policyReport.totalRepositories}
              </Typography>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Compliant:</Typography>
              <Typography variant="body2" style={{ fontWeight: 'bold', color: '#4caf50' }}>
                {policyReport.compliantRepositories}
              </Typography>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Non-Compliant:</Typography>
              <Typography variant="body2" style={{ fontWeight: 'bold', color: '#f44336' }}>
                {policyReport.nonCompliantRepositories}
              </Typography>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Unknown Status:</Typography>
              <Typography variant="body2" style={{ fontWeight: 'bold', color: '#9e9e9e' }}>
                {policyReport.unknownRepositories}
              </Typography>
            </div>
          </div>
          <LinearProgress 
            variant="determinate" 
            value={policyReport.overallComplianceRate}
            style={{ 
              marginTop: '16px',
              backgroundColor: '#f0f0f0',
            }}
          />
          <Typography variant="caption" color="textSecondary" style={{ marginTop: '8px', display: 'block' }}>
            Overall Compliance Rate: {policyReport.overallComplianceRate.toFixed(1)}%
          </Typography>
        </CardContent>
      </Card>
    </Grid>
    <Grid item xs={12} md={6}>
      <Card elevation={1}>
        <CardContent>
          <Typography variant="h6" style={{ marginBottom: '16px' }}>
            Issue Breakdown
          </Typography>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Master Branch Issues:</Typography>
              <Typography variant="body2" style={{ fontWeight: 'bold', color: '#f44336' }}>
                {policyReport.summary.masterBranchIssues}
              </Typography>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Release Branch Issues:</Typography>
              <Typography variant="body2" style={{ fontWeight: 'bold', color: '#f44336' }}>
                {policyReport.summary.releaseBranchIssues}
              </Typography>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Last Updated:</Typography>
              <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                {formatDateShort(policyReport.lastUpdated)}
              </Typography>
            </div>
          </div>
        </CardContent>
      </Card>
    </Grid>
  </Grid>
);

// Policy tabs component
interface PolicyTabsProps {
  policyReport: ProjectBranchPolicyReport;
  tabValue: number;
  onTabChange: (event: React.ChangeEvent<{}>, newValue: number) => void;
  onRepositorySelect: (repository: BranchPolicyCompliance) => void;
}

export const PolicyTabs: React.FC<PolicyTabsProps> = ({ 
  policyReport, 
  tabValue, 
  onTabChange, 
  onRepositorySelect 
}) => (
  <Card>
    <Tabs
      value={tabValue}
      onChange={onTabChange}
      indicatorColor="primary"
      textColor="primary"
      variant="scrollable"
      scrollButtons="auto"
    >
      <Tab label="Repository Compliance" />
      <Tab label="Common Violations" />
      <Tab label="Summary Report" />
    </Tabs>

    <TabPanel value={tabValue} index={0}>
      <RepositoryComplianceList 
        repositoryCompliance={policyReport.repositoryCompliance}
        onRepositorySelect={onRepositorySelect}
      />
    </TabPanel>

    <TabPanel value={tabValue} index={1}>
      <CommonViolations 
        commonViolations={policyReport.summary.commonViolations}
        totalRepositories={policyReport.totalRepositories}
      />
    </TabPanel>

    <TabPanel value={tabValue} index={2}>
      <SummaryReport policyReport={policyReport} />
    </TabPanel>
  </Card>
);

// Policy detail view component
interface PolicyDetailProps {
  repository: BranchPolicyCompliance;
}

export const PolicyDetail: React.FC<PolicyDetailProps> = ({ repository }) => (
  <div>
    <Card style={{ marginBottom: '20px' }}>
      <CardContent>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
          <div>
            <Typography variant="h4" component="h2" style={{ marginBottom: '8px' }}>
              {repository.repositoryName}
            </Typography>
            <Typography variant="body1" color="textSecondary" style={{ marginBottom: '8px' }}>
              Repository Policy Compliance Details
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Last checked: {formatDate(repository.lastChecked)}
            </Typography>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {getComplianceIcon(repository.overallCompliance)}
            {getComplianceChip(repository.overallCompliance)}
          </div>
        </div>
      </CardContent>
    </Card>

    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" component="h3" style={{ marginBottom: '16px' }}>
              Master Branch Compliance
            </Typography>
            <div style={{ marginBottom: '16px' }}>
              {getComplianceChip(repository.masterBranchCompliance.complianceStatus)}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <Typography variant="body2" color="textSecondary">Read-Only Status:</Typography>
                <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                  {repository.masterBranchCompliance.isReadOnly ? 'Protected (Read-Only)' : 'Not Protected'}
                </Typography>
              </div>
              <div>
                <Typography variant="body2" color="textSecondary">Has Contribute Permissions:</Typography>
                <Typography variant="body2" style={{ fontWeight: 'bold', color: repository.masterBranchCompliance.hasContributePermissions ? '#f44336' : '#4caf50' }}>
                  {repository.masterBranchCompliance.hasContributePermissions ? 'Yes (Issue)' : 'No (Good)'}
                </Typography>
              </div>
              {repository.masterBranchCompliance.contributorsWithAccess.length > 0 && (
                <div>
                  <Typography variant="body2" color="textSecondary">Contributors with Access:</Typography>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
                    {repository.masterBranchCompliance.contributorsWithAccess.map((contributor: string, index: number) => (
                      <Chip 
                        key={index}
                        label={contributor}
                        size="small"
                        variant="outlined"
                        style={{ fontSize: '0.7em' }}
                      />
                    ))}
                  </div>
                </div>
              )}
              <div>
                <Typography variant="body2" color="textSecondary">Details:</Typography>
                <Typography variant="body2">
                  {repository.masterBranchCompliance.details}
                </Typography>
              </div>
            </div>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" component="h3" style={{ marginBottom: '16px' }}>
              Release Branch Compliance
            </Typography>
            <div style={{ marginBottom: '16px' }}>
              {getComplianceChip(repository.releaseBranchCompliance.complianceStatus)}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <Typography variant="body2" color="textSecondary">Minimum Reviewers:</Typography>
                <Typography variant="body2" style={{ fontWeight: 'bold', color: repository.releaseBranchCompliance.hasMinimumReviewers ? '#4caf50' : '#f44336' }}>
                  {repository.releaseBranchCompliance.minimumReviewerCount} 
                  {repository.releaseBranchCompliance.hasMinimumReviewers ? ' (✓)' : ' (✗ Need 2+)'}
                </Typography>
              </div>
              <div>
                <Typography variant="body2" color="textSecondary">Reset Votes on Push:</Typography>
                <Typography variant="body2" style={{ fontWeight: 'bold', color: repository.releaseBranchCompliance.resetVotesOnPush ? '#4caf50' : '#f44336' }}>
                  {repository.releaseBranchCompliance.resetVotesOnPush ? 'Enabled (✓)' : 'Disabled (✗)'}
                </Typography>
              </div>
              <div>
                <Typography variant="body2" color="textSecondary">Linked Work Items Required:</Typography>
                <Typography variant="body2" style={{ fontWeight: 'bold', color: repository.releaseBranchCompliance.requiresLinkedWorkItems ? '#4caf50' : '#f44336' }}>
                  {repository.releaseBranchCompliance.requiresLinkedWorkItems ? 'Required (✓)' : 'Not Required (✗)'}
                </Typography>
              </div>
              <div>
                <Typography variant="body2" color="textSecondary">Comment Resolution Required:</Typography>
                <Typography variant="body2" style={{ fontWeight: 'bold', color: repository.releaseBranchCompliance.requiresCommentResolution ? '#4caf50' : '#f44336' }}>
                  {repository.releaseBranchCompliance.requiresCommentResolution ? 'Required (✓)' : 'Not Required (✗)'}
                </Typography>
              </div>
              <div>
                <Typography variant="body2" color="textSecondary">Limited to Squash Merge:</Typography>
                <Typography variant="body2" style={{ fontWeight: 'bold', color: repository.releaseBranchCompliance.limitedToSquashMerge ? '#4caf50' : '#f44336' }}>
                  {repository.releaseBranchCompliance.limitedToSquashMerge ? 'Limited (✓)' : 'Not Limited (✗)'}
                </Typography>
              </div>
            </div>
          </CardContent>
        </Card>
      </Grid>

      {/* Policy Details */}
      {repository.releaseBranchCompliance.policies.length > 0 && (
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" component="h3" style={{ marginBottom: '16px' }}>
                Applied Policies ({repository.releaseBranchCompliance.policies.length})
              </Typography>
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {repository.releaseBranchCompliance.policies.map((policy: any, index: number) => (
                  <Card key={index} variant="outlined" style={{ marginBottom: '12px' }}>
                    <CardContent style={{ padding: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                          {policy.type?.displayName || 'Unknown Policy'}
                        </Typography>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <Chip 
                            size="small" 
                            label={policy.isEnabled ? 'Enabled' : 'Disabled'}
                            style={{ 
                              backgroundColor: policy.isEnabled ? '#4caf50' : '#f44336', 
                              color: 'white' 
                            }}
                          />
                          <Chip 
                            size="small" 
                            label={policy.isBlocking ? 'Blocking' : 'Non-blocking'}
                            variant="outlined"
                          />
                        </div>
                      </div>
                      <Typography variant="caption" color="textSecondary">
                        Policy ID: {policy.id} • Revision: {policy.revision}
                      </Typography>
                      {policy.settings?.displayName && (
                        <Typography variant="body2" style={{ marginTop: '8px' }}>
                          {policy.settings.displayName}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* Compliance Issues */}
      {repository.releaseBranchCompliance.details.length > 0 && (
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" component="h3" style={{ marginBottom: '16px' }}>
                Compliance Issues & Details
              </Typography>
              <div>
                {repository.releaseBranchCompliance.details.map((detail: string, index: number) => (
                  <div key={index} style={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    padding: '8px 0',
                    borderBottom: index < repository.releaseBranchCompliance.details.length - 1 ? '1px solid #f0f0f0' : 'none'
                  }}>
                    <div style={{ marginRight: '12px' }}>
                      {detail.includes('compliant') ? 
                        <CheckCircle style={{ color: '#4caf50', fontSize: 20 }} /> :
                        <ErrorOutline style={{ color: '#f44336', fontSize: 20 }} />
                      }
                    </div>
                    <Typography variant="body2" style={{ flex: 1 }}>
                      {detail}
                    </Typography>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </Grid>
      )}
    </Grid>
  </div>
);